import { useContext, useEffect, useState } from "react";
import { useImmer } from "use-immer";
import { useParams, useNavigate } from "react-router-dom";
import { errorMessage, successMessage } from "../../../utils";
import {
  getStampCardDetailsApi,
  deleteStampCardApi,
  stampStatusChangeApi,
} from "../api";
import { AppContext } from "../../../store";
import { usePermission } from "../../../shared";

export const useStampCard = () => {
  const { id } = useParams();
  const { appState } = useContext(AppContext);
  const navigate = useNavigate();
  const { globalData } = appState;
  const isEditable = usePermission("stamp-card-modify");
  const isDeletable = usePermission("stamp-card-delete");

  const [state, setState] = useImmer({
    isLoading: true,
    isSaving: false,
    isOpen: false,
    data: {
      name: "",
      description: "",
      total_stamps: "",
      cycle_validity: "",
      created_at: "",
      status: 0,
    },
    deleteModalOpen: false,
    isDeleteBtnLoad: false,
    publishModalOpen: false,
    unpublishModalOpen: false,
  });

  // Fetch stamp card details
  useEffect(() => {
    if (id) {
      getStampCardDetails(id);
    }
  }, [id]);

  const getStampCardDetails = async (stampCardId) => {
    try {
      setState((draft) => {
        draft.isLoading = true;
      });
      const res = await getStampCardDetailsApi(stampCardId);
      if (res.success) {
        setState((draft) => {
          draft.data = res.data;
          draft.isLoading = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to load details");
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const toggleModal = (stampCardId, from) => {
    setState((draft) => {
      draft.isOpen = !draft.isOpen;
    });
  };

  const handleModal = (type, state, data) => {
    switch (type) {
      case "delete":
        setState((draft) => {
          draft.deleteModalOpen = state;
        });
        break;
      case "publish":
        setState((draft) => {
          draft.publishModalOpen = state;
        });
        break;
      case "unpublish":
        setState((draft) => {
          draft.unpublishModalOpen = state;
        });
        break;
    }
  };

  const handleDeleteStampCard = async () => {
    if (!id) return;

    try {
      setState((draft) => {
        draft.isDeleteBtnLoad = true;
      });

      const res = await deleteStampCardApi(id);

      if (res.success) {
        navigate("/stamp-cards");
        successMessage(res.message);
      } else {
        errorMessage(res.message);
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to delete stamp card",
      );
    } finally {
      setState((draft) => {
        draft.deleteModalOpen = false;
        draft.isDeleteBtnLoad = false;
      });
    }
  };

  const handleChangeStampStatus = async (statusID) => {
    if (!id) return;

    try {
      setState((draft) => {
        draft.isSaving = true;
      });
      let res = await stampStatusChangeApi(id, statusID);
      if (res.success) {
        successMessage(res.message);
        getStampCardDetails(id);
      } else {
        errorMessage(res.message);
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to publish stamp card",
      );
    } finally {
      setState((draft) => {
        draft.publishModalOpen = false;
        draft.unpublishModalOpen = false;
        draft.isSaving = false;
      });
    }
  };

  return {
    state,
    globalData,
    toggleModal,
    isEditable,
    isDeletable,
    handleModal,
    handleDeleteStampCard,
    handleChangeStampStatus,
  };
};